// React Query hooks for manuscript CRUD operations
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Manuscript, Series, Collection } from "@/types/database";
import { useRepository } from "@/contexts/PlatformContext";

// Query keys
export const manuscriptKeys = {
  all: ["manuscripts"] as const,
  detail: (id: string) => ["manuscripts", id] as const,
  series: ["series"] as const,
  seriesDetail: (id: string) => ["series", id] as const,
  collections: ["collections"] as const,
  collectionDetail: (id: string) => ["collections", id] as const,
};

// ============ Manuscripts ============

export function useManuscripts() {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.all,
    queryFn: () => repository.getAllManuscripts(),
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useManuscript(id: string | undefined) {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.detail(id ?? ""),
    queryFn: () => (id ? repository.getManuscript(id) : Promise.resolve(undefined)),
    enabled: !!id,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

export function useCreateManuscript() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (manuscript: Manuscript) => repository.putManuscript(manuscript),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.all });
    },
  });
}

export function useUpdateManuscript() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: async (updates: Partial<Manuscript> & { id: string }) => {
      // Fetch current manuscript and merge with updates
      const current = await repository.getManuscript(updates.id);
      if (!current) throw new Error(`Manuscript ${updates.id} not found`);
      const merged: Manuscript = { ...current, ...updates };
      return repository.putManuscript(merged);
    },
    onSuccess: (_, updates) => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.all });
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.detail(updates.id) });
    },
  });
}

export function useDeleteManuscript() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: async ({ id, purgeCloud = true }: { id: string; purgeCloud?: boolean }) => {
      await repository.deleteManuscript(id, purgeCloud);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.all });
    },
  });
}

// ============ Series ============

export function useSeries() {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.series,
    queryFn: () => repository.getAllSeries(),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

export function useSeriesDetail(id: string | undefined) {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.seriesDetail(id ?? ""),
    queryFn: () => (id ? repository.getSeries(id) : Promise.resolve(undefined)),
    enabled: !!id,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

export function useCreateSeries() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (series: Series) => repository.putSeries(series),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.series });
    },
  });
}

export function useUpdateSeries() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: async (updates: Partial<Series> & { id: string }) => {
      const current = await repository.getSeries(updates.id);
      if (!current) throw new Error(`Series ${updates.id} not found`);
      const merged: Series = { ...current, ...updates };
      return repository.putSeries(merged);
    },
    onSuccess: (_, updates) => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.series });
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.seriesDetail(updates.id) });
    },
  });
}

export function useDeleteSeries() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (id: string) => repository.deleteSeries(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.series });
    },
  });
}

// ============ Collections ============

export function useCollections() {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.collections,
    queryFn: () => repository.getAllCollections(),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

export function useCollectionDetail(id: string | undefined) {
  const repository = useRepository();
  return useQuery({
    queryKey: manuscriptKeys.collectionDetail(id ?? ""),
    queryFn: () => (id ? repository.getCollection(id) : Promise.resolve(undefined)),
    enabled: !!id,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

export function useCreateCollection() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (collection: Collection) => repository.putCollection(collection),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.collections });
    },
  });
}

export function useUpdateCollection() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: async (updates: Partial<Collection> & { id: string }) => {
      const current = await repository.getCollection(updates.id);
      if (!current) throw new Error(`Collection ${updates.id} not found`);
      const merged: Collection = { ...current, ...updates };
      return repository.putCollection(merged);
    },
    onSuccess: (_, updates) => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.collections });
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.collectionDetail(updates.id) });
    },
  });
}

export function useDeleteCollection() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (id: string) => repository.deleteCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manuscriptKeys.collections });
    },
  });
}

// ============ Combined Library Data ============

export function useLibraryData() {
  const manuscriptsQuery = useManuscripts();
  const seriesQuery = useSeries();
  const collectionsQuery = useCollections();

  const isLoading = manuscriptsQuery.isLoading || seriesQuery.isLoading || collectionsQuery.isLoading;
  const isError = manuscriptsQuery.isError || seriesQuery.isError || collectionsQuery.isError;

  const manuscripts = manuscriptsQuery.data ?? [];
  const series = seriesQuery.data ?? [];
  const collections = collectionsQuery.data ?? [];

  const manuscriptMap = useMemo(() => {
    return new Map(manuscripts.map((m) => [m.id, m]));
  }, [manuscripts]);

  // Derive standalones
  const standalones = useMemo(() => {
    return manuscripts.filter((m) => m.type === "standalone");
  }, [manuscripts]);

  // Derive series with manuscripts
  const seriesData = useMemo(() => {
    return series.map((s) => ({
      series: s,
      manuscripts: s.manuscripts
        .map((id) => manuscriptMap.get(id))
        .filter((m): m is Manuscript => m !== undefined)
        .sort((a, b) => (a.seriesOrder ?? 0) - (b.seriesOrder ?? 0)),
    }));
  }, [series, manuscriptMap]);

  // Derive collections with manuscripts
  const collectionsData = useMemo(() => {
    return collections.map((c) => ({
      collection: c,
      manuscripts: c.manuscripts
        .map((id) => manuscriptMap.get(id))
        .filter((m): m is Manuscript => m !== undefined),
    }));
  }, [collections, manuscriptMap]);

  return {
    manuscripts,
    series,
    collections,
    standalones,
    seriesData,
    collectionsData,
    isLoading,
    isError,
    refetch: () => {
      manuscriptsQuery.refetch();
      seriesQuery.refetch();
      collectionsQuery.refetch();
    },
  };
}
